import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import Lozenge from "@atlaskit/lozenge";

import { templateCategorySectionRelayFragment$key } from "@/__generated__/templateCategorySectionRelayFragment.graphql";
import { TemplateType } from "@/content/templates/template.types";

export type TemplateCategorySectionProps = {
    template: templateCategorySectionRelayFragment$key;
};

const WHITEBOARD_TEMPLATE_ID = "1HlskYfRNciSnQ02SDhCPs";

const templateCategorySectionStyles = css({
    display: "flex",
    flexDirection: "column"
});

const headingStyles = css({
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#505F79",
    fontSize: ".75rem",
    fontWeight: 400,
    letterSpacing: "1px",
    marginBlockEnd: "24px",
    marginBlockStart: "28px"
});

const categoryContainerStyles = css({
    display: "inline-flex",
    height: "32px",
    paddingBlockEnd: "16px"
});

const TemplateCategorySectionRelayFragment = graphql`
    fragment templateCategorySectionRelayFragment on ContentPlatformTemplate {
        templateType {
            id
            templateTypeName
        }
        categories: category {
            id
            name
            urlSlug
        }
    }
`;

export const TemplateCategorySection = ({
    template
}: TemplateCategorySectionProps) => {
    const { categories, templateType } = useFragment(
        TemplateCategorySectionRelayFragment,
        template
    );

    const intl = useIntl();

    const categoryText = intl.formatMessage({
        id: "ConfluenceTemplateMessages.categories.heading",
        defaultMessage: "CATEGORIES"
    });

    const experimentCategories = (categories ?? []).filter(
        (category) =>
            category.urlSlug === "recommended" ||
            category.urlSlug === "project-management" ||
            category.urlSlug === "software-development-and-it" ||
            category.urlSlug === "product-management" ||
            category.urlSlug === "marketing-and-sales" ||
            category.urlSlug === "design" ||
            category.urlSlug === "finance-and-operations" ||
            category.urlSlug === "human-resources"
    );

    const type = templateType?.[0] ?? {
        id: null,
        templateTypeName: null
    };

    const templateTypeKey =
        type?.id === WHITEBOARD_TEMPLATE_ID
            ? TemplateType.Whiteboard
            : TemplateType.Page;

    const templateTypeLabel = type?.templateTypeName ?? TemplateType.Page;

    return (
        <section css={templateCategorySectionStyles}>
            <h1 css={headingStyles}>{categoryText}</h1>
            {experimentCategories.map((category) => (
                <div key={category.id} css={categoryContainerStyles}>
                    <Lozenge appearance="inprogress">{category.name}</Lozenge>
                </div>
            ))}
            <div key={templateTypeKey} css={categoryContainerStyles}>
                <Lozenge
                    appearance={
                        templateTypeKey === TemplateType.Page
                            ? "inprogress"
                            : "success"
                    }
                    isBold
                >
                    <FormattedMessage
                        id="confluence.templates.detail.template.type"
                        defaultMessage={`{templateType} template`}
                        values={{ templateType: templateTypeLabel }}
                    />
                </Lozenge>
            </div>
        </section>
    );
};
